import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowClockwise, Warning, PaperPlaneTilt, X as XIcon, Sparkle, CheckCircle,
  CurrencyDollar, ArrowUUpLeft, Clock, EnvelopeSimple, Users, ChatCircleText, EyeSlash, Eye, Cursor, ArrowsClockwise
} from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

function fmtDaysAgo(n) {
  if (n === null || n === undefined) return "—";
  if (n === 0) return "today";
  if (n === 1) return "1d ago";
  if (n < 30) return `${n}d ago`;
  const m = Math.floor(n / 30);
  if (m < 12) return `${m}mo ago`;
  return `${Math.floor(n / 365)}y ago`;
}

const TONE_OPTIONS = [
  { value: "warm", label: "Warm", desc: "Personal, empathetic — like a fellow trucker" },
  { value: "direct", label: "Direct", desc: "Value-focused, cut to the chase" },
  { value: "urgent", label: "Urgent", desc: "'Offer expires in 7 days' pressure" },
];

function ComposeModal({ open, candidate, onClose, onSent, adminEmail, isFollowup = false }) {
  const [tone, setTone] = useState("warm");
  const [discount, setDiscount] = useState(30);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);
  const [replyTo, setReplyTo] = useState(adminEmail || "");

  useEffect(() => {
    if (open) {
      setDraft(null);
      setTone("warm");
      setDiscount(30);
      setReplyTo(adminEmail || "");
    }
  }, [open, adminEmail]);

  if (!open || !candidate) return null;

  const generate = async () => {
    setDrafting(true);
    try {
      const { data } = await client.post("/admin/win-back/draft", {
        user_id: candidate.id,
        discount_pct: Number(discount) || 30,
        tone,
        is_followup: isFollowup,
      });
      setDraft(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drafting failed");
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    if (!draft?.subject || !draft?.body) {
      toast.error("Generate a draft first");
      return;
    }
    setSending(true);
    try {
      const { data } = await client.post("/admin/win-back/send", {
        user_id: candidate.id,
        subject: draft.subject,
        body: draft.body,
        reply_to: replyTo || undefined,
        is_followup: isFollowup,
      });
      if (data?.status === "sent") {
        const via = data.provider === "gmail_smtp" ? " (via your Gmail)" : data.provider === "resend" ? " (via Resend)" : "";
        toast.success(`✅ ${isFollowup ? "Follow-up" : "Email"} sent to ${candidate.email}${via}`);
        onSent?.();
        onClose();
      } else {
        toast.error(data?.error || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-testid="winback-compose-modal">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0F0F0F] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
              {isFollowup ? "Win-Back — Follow-up" : "Win-Back Campaign"}
            </div>
            <h2 className="text-xl font-display font-semibold text-white">
              {candidate.full_name || candidate.email}
            </h2>
            <p className="text-xs text-neutral-500 mt-1">
              {candidate.email} · {candidate.company_name || "—"} · signup {fmtDaysAgo(candidate.days_since_signup)}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="winback-modal-close"
            className="text-neutral-500 hover:text-white shrink-0"
          >
            <XIcon size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {!draft ? (
            <>
              {/* Tone selector */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-3">Tone</label>
                <div className="grid grid-cols-3 gap-2">
                  {TONE_OPTIONS.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setTone(t.value)}
                      data-testid={`winback-tone-${t.value}`}
                      className={`text-left p-3 rounded-lg border transition-all ${
                        tone === t.value
                          ? "border-[#FF5A1F]/60 bg-[#FF5A1F]/[0.08] ring-1 ring-[#FF5A1F]/30"
                          : "border-white/10 hover:border-white/25"
                      }`}
                    >
                      <div className="text-white text-sm font-semibold">{t.label}</div>
                      <div className="text-[11px] text-neutral-400 mt-0.5 leading-snug">{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              {/* Discount */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">Discount to Offer</label>
                <div className="flex gap-2 items-center">
                  <div className="relative flex-1 max-w-[140px]">
                    <input
                      type="number"
                      min={10}
                      max={50}
                      step={5}
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      data-testid="winback-discount-input"
                      className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 px-3 pr-8 focus:outline-none focus:border-[#FF5A1F]/60"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">%</span>
                  </div>
                  <span className="text-xs text-neutral-500">off first month</span>
                </div>
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  {[20, 30, 50].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setDiscount(v)}
                      data-testid={`winback-discount-${v}`}
                      className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${
                        Number(discount) === v
                          ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/15 text-[#FF5A1F]"
                          : "border-white/10 text-neutral-400 hover:text-white hover:border-white/25"
                      }`}
                    >
                      {v}%
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={generate}
                disabled={drafting}
                data-testid="winback-generate-btn"
                className="w-full rounded-full py-3 text-sm font-semibold bg-[#007AFF] text-white hover:bg-[#3392FF] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Sparkle size={14} weight="fill" />
                {drafting ? "AI drafting…" : "Generate personalized email"}
              </button>
            </>
          ) : (
            <>
              {/* Draft preview + edit */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">Subject</label>
                <input
                  type="text"
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  data-testid="winback-subject-input"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">Body (edit before sending)</label>
                <textarea
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  rows={10}
                  data-testid="winback-body-textarea"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 px-3 focus:outline-none focus:border-[#FF5A1F]/60 font-mono leading-relaxed"
                />
                <p className="text-[11px] text-neutral-500 mt-1.5">
                  A "Reactivate my trial →" CTA button auto-appends to the email, linking to /pricing with UTM tracking.
                </p>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">Reply-To (broker replies come to this inbox)</label>
                <input
                  type="email"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  data-testid="winback-replyto-input"
                  placeholder={adminEmail}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setDraft(null)}
                  data-testid="winback-back-btn"
                  className="rounded-full px-4 py-2.5 text-sm font-medium border border-white/10 text-neutral-400 hover:text-white hover:border-white/25 transition-colors inline-flex items-center gap-1.5"
                >
                  <ArrowUUpLeft size={13} /> Regenerate
                </button>
                <button
                  onClick={send}
                  disabled={sending}
                  data-testid="winback-send-btn"
                  className="flex-1 rounded-full py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <PaperPlaneTilt size={13} weight="fill" />
                  {sending ? "Sending…" : `Send to ${candidate.email}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminWinBackPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showContacted, setShowContacted] = useState(false);
  const [followupMode, setFollowupMode] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/win-back/candidates");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load win-back candidates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const skipCandidate = async (candidate) => {
    const label = candidate.full_name || candidate.email;
    if (!window.confirm(`Skip "${label}"? They will no longer appear in the Win-Back list. (Undo requires a database change.)`)) return;
    try {
      await client.post(`/admin/win-back/skip/${candidate.id}`);
      toast.success(`Skipped ${label}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Skip failed");
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="card-surface p-8 text-center" data-testid="admin-winback-forbidden">
        <Warning size={40} weight="fill" className="text-red-400 mx-auto mb-3" />
        <h1 className="text-xl font-semibold text-white mb-2">Admin only</h1>
        <p className="text-neutral-400 text-sm">This tool is restricted to admin accounts.</p>
      </div>
    );
  }

  const list = showContacted ? (data?.already_contacted || []) : (data?.candidates || []);

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8" data-testid="admin-winback-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/30 bg-[#FF5A1F]/10 text-[#FF5A1F] px-3 py-1 text-xs font-semibold mb-3">
            <CurrencyDollar size={12} weight="fill" /> WIN-BACK CAMPAIGN
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">
            Recover expired trials
          </h1>
          <p className="text-neutral-400 mt-2 text-sm sm:text-base max-w-2xl">
            AI-drafted personalized emails to bring back users who churned. Even 5-10% recovery = real MRR back on the table.
          </p>
        </div>
        <button
          onClick={reload}
          disabled={loading}
          data-testid="winback-refresh-btn"
          className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 hover:border-white/25 transition-colors disabled:opacity-50"
        >
          <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="card-surface p-5" data-testid="winback-stat-candidates">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
            <Users size={13} /> Awaiting Contact
          </div>
          <div className="text-3xl font-display font-semibold tracking-tight text-[#FF5A1F]">
            {data?.total_candidates ?? 0}
          </div>
          <div className="text-xs text-neutral-500 mt-2">Users who never activated a plan</div>
        </div>
        <div className="card-surface p-5" data-testid="winback-stat-contacted">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
            <PaperPlaneTilt size={13} /> Already Contacted
          </div>
          <div className="text-3xl font-display font-semibold tracking-tight text-emerald-400">
            {data?.total_contacted ?? 0}
          </div>
          <div className="text-xs text-neutral-500 mt-2">Sent at least one win-back email</div>
        </div>
        <div className="card-surface p-5" data-testid="winback-stat-trialing">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
            <Clock size={13} /> Skipped (Still Trialing)
          </div>
          <div className="text-3xl font-display font-semibold tracking-tight text-white">
            {data?.still_trialing_skipped ?? 0}
          </div>
          <div className="text-xs text-neutral-500 mt-2">Trial still active — leave them alone</div>
        </div>
      </div>

      {/* Toggle candidates vs contacted */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowContacted(false)}
          data-testid="winback-tab-candidates"
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
            !showContacted
              ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/15 text-[#FF5A1F]"
              : "border-white/10 text-neutral-400 hover:text-white hover:border-white/25"
          }`}
        >
          Awaiting ({data?.total_candidates ?? 0})
        </button>
        <button
          onClick={() => setShowContacted(true)}
          data-testid="winback-tab-contacted"
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
            showContacted
              ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-400"
              : "border-white/10 text-neutral-400 hover:text-white hover:border-white/25"
          }`}
        >
          Contacted ({data?.total_contacted ?? 0})
        </button>
      </div>

      {/* Candidates table */}
      <div className="card-surface p-2 sm:p-4" data-testid="winback-list">
        {loading ? (
          <div className="text-neutral-500 text-sm py-12 text-center">Loading…</div>
        ) : list.length === 0 ? (
          <div className="text-neutral-500 text-sm py-12 text-center border border-dashed border-white/10 rounded-lg">
            {showContacted
              ? "No emails sent yet. Head to the Awaiting tab to start."
              : "🎉 No candidates! Every churned user has been contacted."}
          </div>
        ) : (
          <div className="divide-y divide-white/[0.05]">
            {list.map((c) => (
              <div
                key={c.id}
                data-testid={`winback-row-${c.email}`}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium text-sm truncate">
                    {c.full_name || c.email}
                  </div>
                  <div className="text-xs text-neutral-500 truncate">
                    {c.email}
                    {c.company_name ? ` · ${c.company_name}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <div className="text-xs text-neutral-400 whitespace-nowrap">
                      {c.has_trial_end ? "Trial ended" : "Signed up"} {fmtDaysAgo(c.trial_expired_days_ago)}
                    </div>
                    {showContacted && c.win_back_sent_at && (
                      <div className="text-[10px] text-neutral-500 mt-0.5">
                        Emailed {fmtDaysAgo(Math.floor((Date.now() - new Date(c.win_back_sent_at)) / (1000 * 60 * 60 * 24)))}
                      </div>
                    )}
                  </div>
                  {showContacted && (
                    <div className="flex items-center gap-1.5" data-testid={`winback-tracking-${c.email}`}>
                      <span
                        title={c.first_opened_at ? `First opened ${new Date(c.first_opened_at).toLocaleString()}` : "Not opened yet"}
                        className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border ${
                          (c.open_count || 0) > 0
                            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                            : "border-white/10 text-neutral-500"
                        }`}
                      >
                        <Eye size={11} weight={(c.open_count || 0) > 0 ? "fill" : "regular"} />
                        {c.open_count || 0}
                      </span>
                      <span
                        title={c.first_clicked_at ? `First clicked ${new Date(c.first_clicked_at).toLocaleString()}` : "Not clicked yet"}
                        className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border ${
                          (c.click_count || 0) > 0
                            ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/15 text-[#FF5A1F]"
                            : "border-white/10 text-neutral-500"
                        }`}
                      >
                        <Cursor size={11} weight={(c.click_count || 0) > 0 ? "fill" : "regular"} />
                        {c.click_count || 0}
                      </span>
                    </div>
                  )}
                  {!showContacted && (
                    <>
                      <button
                        onClick={() => { setFollowupMode(false); setSelected(c); }}
                        data-testid={`winback-compose-btn-${c.email}`}
                        className="rounded-full px-3 py-1.5 text-xs font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] transition-colors inline-flex items-center gap-1.5 shrink-0"
                      >
                        <ChatCircleText size={12} weight="fill" /> Draft
                      </button>
                      <button
                        onClick={() => skipCandidate(c)}
                        data-testid={`winback-skip-btn-${c.email}`}
                        title="Skip this user (test account, personal account, etc.)"
                        className="rounded-full p-1.5 text-neutral-500 hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0"
                      >
                        <EyeSlash size={14} />
                      </button>
                    </>
                  )}
                  {showContacted && c.followup_eligible && (
                    <button
                      onClick={() => { setFollowupMode(true); setSelected(c); }}
                      data-testid={`winback-followup-btn-${c.email}`}
                      title={`Send a follow-up (opened but didn't click after ${c.days_since_first_send}d)`}
                      className="rounded-full px-3 py-1.5 text-xs font-semibold bg-[#007AFF] text-white hover:bg-[#3392FF] transition-colors inline-flex items-center gap-1.5 shrink-0"
                    >
                      <ArrowsClockwise size={12} weight="fill" /> Follow up
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ComposeModal
        open={!!selected}
        candidate={selected}
        onClose={() => { setSelected(null); setFollowupMode(false); }}
        onSent={reload}
        adminEmail={user?.email}
        isFollowup={followupMode}
      />
    </div>
  );
}
